/**
 * /mrp/clamp-forecast — perpetual forecast grid for clamp/fiber categories.
 *
 * One row per `mrp.clamp_category`, one column per calendar month in a sliding
 * window. Each cell shows the editable forecast quantity above the running
 * total shipped from any linked POs (so the planner can compare forecast vs
 * actual without leaving the page). Selecting a cell opens a side panel with
 * revision history, linked POs, and a "Create PO" action (single line for
 * non-kit categories, BoM-style multi-line for kit categories).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  apiFetch,
} from "@/lib/api";
import {
  HUB_CACHE_CHANNEL,
} from "@/lib/hubCacheChannels";
import {
  useHubCacheStream,
} from "@/lib/useHubCacheStream";
import {
  AnchoredDetailCallout,
  buildAttributeAnchorSelector,
} from "@/components/ui/AnchoredDetailCallout";
import {
  Button,
} from "@/components/ui/Button";
import {
  Card,
  CardBody,
  CardHeader,
} from "@/components/ui/Card";
import {
  CardTitleWithHelp,
  PageTitleWithHelp,
} from "@/components/ui/title-help";
import {
  useHubModuleWrite,
} from "@/lib/useHubModuleWrite";
import {
  useHubToast,
} from "@/components/ui/HubToast";
import {
  buildClampForecastMonthPoPlan,
  type ClampForecastMonthPoPlan,
} from "@integrator-src/mrp/clamp-forecast-po-lines";
import {
  firstOfThisMonth,
  formatMonth,
  clampForecastCacheKey,
  type MrpSkuHoverHintFields,
} from "@/lib/mrp";
import {
  CALLOUT_WIDTH,
  ClampForecastMonthCreatePoModal,
  ClampForecastSidePanel,
  ClampForecastTable,
  INVENTORY_HOVER_HINT,
  PAGE_HOVER_HINT,
  PAST_MONTHS,
  START_MONTH_OPTIONS_AFTER,
  START_MONTH_OPTIONS_BEFORE,
  START_MONTH_SELECT_CLASS,
  buildStartMonthOptions,
  cellKey,
  clampForecastDerivedDemandQueryKey,
  clampForecastShellQueryKey,
  describeApiError,
  indexCells,
  indexDerivedDemandByCell,
  indexDerivedDemandSkusByCategory,
  indexGhosts,
  indexInventoryBySku,
  indexPoLinks,
  initialClampForecastStartMonth,
  patchCategoryPoLinksInShell,
  patchClampForecastGhostInShell,
  patchClampForecastGridCell,
  rechainCategoryRollForwardInCache,
  removeClampForecastGridCell,
  writeClampForecastStartMonthToSession,
} from "./clampForecastGridSupport";
import type {
  ClampForecastCellRow,
  ClampForecastDerivedDemandResponse,
  ClampForecastGhostRow,
  ClampForecastGridResponse,
  ClampForecastKitItemRow,
  ClampForecastPoLinkRow,
  ClampForecastShellResponse,
} from "./clampForecastGridSupport";

export function ClampForecast() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const { showToast } = useHubToast();
  const [activeCell, setActiveCell] = useState<{ category_code: string; plan_month: string } | null>(null);
  const [activeMonthPo, setActiveMonthPo] = useState<string | null>(null);
  const [draggingPoLinkId, setDraggingPoLinkId] = useState<number | null>(null);
  const [poLinkDropHighlightMonth, setPoLinkDropHighlightMonth] = useState<string | null>(null);
  const [startMonth, setStartMonth] = useState<string>(initialClampForecastStartMonth);
  const [refreshingFrom, setRefreshingFrom] = useState<{ categoryCode: string; fromMonth: string } | null>(
    null,
  );
  const startMonthOptions = useMemo(
    () => buildStartMonthOptions(firstOfThisMonth(), START_MONTH_OPTIONS_BEFORE, START_MONTH_OPTIONS_AFTER),
    [],
  );

  const shellQueryKey = useMemo(
    () => clampForecastShellQueryKey(startMonth, PAST_MONTHS),
    [startMonth],
  );
  const derivedQueryKey = useMemo(
    () => clampForecastDerivedDemandQueryKey(startMonth, PAST_MONTHS),
    [startMonth],
  );

  const shellQuery = useQuery({
    queryKey: shellQueryKey,
    queryFn: () =>
      apiFetch<ClampForecastShellResponse>("/api/mrp/clamp-forecast/shell", {
        query: {
          start_month: startMonth,
          past_months: PAST_MONTHS,
          background: "1",
        },
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const derivedQuery = useQuery({
    queryKey: derivedQueryKey,
    queryFn: () =>
      apiFetch<ClampForecastDerivedDemandResponse>("/api/mrp/clamp-forecast/derived-demand", {
        query: {
          start_month: startMonth,
          past_months: PAST_MONTHS,
          background: "1",
        },
      }),
    enabled: shellQuery.isSuccess,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const clampForecastHubCacheChannel =
    shellQuery.data != null
      ? HUB_CACHE_CHANNEL.mrpClampForecast(
          clampForecastCacheKey(shellQuery.data.start_month, shellQuery.data.end_month_exclusive),
        )
      : null;

  useHubCacheStream({
    channels: clampForecastHubCacheChannel == null ? [] : [clampForecastHubCacheChannel],
    enabled: clampForecastHubCacheChannel != null,
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: shellQueryKey });
        void queryClient.invalidateQueries({ queryKey: derivedQueryKey });
      }
    },
  });

  const refreshCategoryAfterPoChange = useCallback(
    async (categoryCode: string, fromPlanMonth: string) => {
      setRefreshingFrom({ categoryCode, fromMonth: fromPlanMonth });
      try {
        const payload = await apiFetch<{
          category_code: string;
          po_links: ClampForecastPoLinkRow[];
        }>(`/api/mrp/clamp-forecast/categories/${encodeURIComponent(categoryCode)}/po-links`, {
          query: {
            start_month: startMonth,
            past_months: PAST_MONTHS,
          },
        });
        patchCategoryPoLinksInShell(queryClient, shellQueryKey, categoryCode, payload.po_links);
        rechainCategoryRollForwardInCache(queryClient, shellQueryKey, derivedQueryKey, categoryCode);
      } catch (error) {
        showToast({ message: describeApiError(error), tone: "error" });
      } finally {
        setRefreshingFrom((current) =>
          current?.categoryCode === categoryCode && current.fromMonth === fromPlanMonth ? null : current,
        );
      }
    },
    [derivedQueryKey, queryClient, shellQueryKey, showToast, startMonth],
  );

  const handleCellSaved = useCallback(
    (cell: ClampForecastCellRow) => {
      patchClampForecastGridCell(queryClient, shellQueryKey, cell);
      void queryClient.invalidateQueries({
        queryKey: ["mrp", "clamp-forecast", "revisions", cell.id],
      });
      rechainCategoryRollForwardInCache(queryClient, shellQueryKey, derivedQueryKey, cell.category_code);
    },
    [derivedQueryKey, queryClient, shellQueryKey],
  );

  const handleCellCleared = useCallback(
    (categoryCode: string, planMonth: string) => {
      removeClampForecastGridCell(queryClient, shellQueryKey, categoryCode, planMonth);
      rechainCategoryRollForwardInCache(queryClient, shellQueryKey, derivedQueryKey, categoryCode);
    },
    [derivedQueryKey, queryClient, shellQueryKey],
  );

  const handleGhostSaved = useCallback(
    (ghost: ClampForecastGhostRow | null, categoryCode: string, planMonth: string) => {
      patchClampForecastGhostInShell(queryClient, shellQueryKey, ghost, categoryCode, planMonth);
      rechainCategoryRollForwardInCache(queryClient, shellQueryKey, derivedQueryKey, categoryCode);
    },
    [derivedQueryKey, queryClient, shellQueryKey],
  );

  const clearGhostNumbersMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ deleted_count: number }>("/api/mrp/clamp-forecast/ghosts", {
        method: "DELETE",
      }),
    onSuccess: (data) => {
      queryClient.setQueryData<ClampForecastShellResponse>(shellQueryKey, (old) =>
        old == null ? old : { ...old, ghosts: [] },
      );
      void queryClient.invalidateQueries({ queryKey: derivedQueryKey });
      showToast({
        message:
          data.deleted_count > 0
            ? `Cleared ${data.deleted_count} ghost shipment adjustment${data.deleted_count === 1 ? "" : "s"}.`
            : "No ghost shipment adjustments were set.",
        tone: "success",
      });
    },
    onError: (error) => showToast({ message: describeApiError(error), tone: "error" }),
  });

  const handleGridStale = useCallback(
    (categoryCode: string, fromPlanMonth: string) => {
      void refreshCategoryAfterPoChange(categoryCode, fromPlanMonth);
    },
    [refreshCategoryAfterPoChange],
  );

  const handleMonthGridStale = useCallback(
    async (planMonth: string, categoryCodes: string[]) => {
      for (const categoryCode of categoryCodes) {
        await refreshCategoryAfterPoChange(categoryCode, planMonth);
      }
    },
    [refreshCategoryAfterPoChange],
  );

  useEffect(() => {
    setActiveCell(null);
    setActiveMonthPo(null);
    setDraggingPoLinkId(null);
    setPoLinkDropHighlightMonth(null);
  }, [startMonth]);

  const movePoLinkMutation = useMutation({
    mutationFn: async ({
      cellId,
      linkId,
      planMonth,
    }: {
      cellId: number;
      linkId: number;
      planMonth: string;
    }) =>
      apiFetch<{
        target_plan_month: string;
        target_cell: ClampForecastCellRow;
      }>(`/api/mrp/clamp-forecast/cells/${cellId}/po-links/${linkId}`, {
        method: "PATCH",
        body: { plan_month: planMonth },
      }),
    onSuccess: async (data) => {
      if (activeCell == null || activeCategoryRow == null) return;
      patchClampForecastGridCell(queryClient, shellQueryKey, data.target_cell);
      await refreshCategoryAfterPoChange(activeCategoryRow.code, activeCell.plan_month);
      setActiveCell({
        category_code: activeCategoryRow.code,
        plan_month: data.target_plan_month,
      });
      setDraggingPoLinkId(null);
      setPoLinkDropHighlightMonth(null);
      showToast({
        message: `Moved purchase order link to ${formatMonth(data.target_plan_month)}.`,
        tone: "success",
      });
    },
    onError: (error) => {
      setDraggingPoLinkId(null);
      setPoLinkDropHighlightMonth(null);
      showToast({ message: describeApiError(error), tone: "error" });
    },
  });

  const shell = shellQuery.data;
  const derivedDemandLoading = shell != null && derivedQuery.isFetching;
  const grid = useMemo((): ClampForecastGridResponse | null => {
    if (shell == null) return null;
    return {
      ...shell,
      derived_demand: derivedQuery.data?.derived_demand ?? [],
    };
  }, [derivedQuery.data?.derived_demand, shell]);
  const cellsIndex = useMemo(() => indexCells(grid?.cells ?? []), [grid?.cells]);
  const ghostsIndex = useMemo(() => indexGhosts(grid?.ghosts ?? []), [grid?.ghosts]);
  const poLinksIndex = useMemo(() => indexPoLinks(grid?.po_links ?? []), [grid?.po_links]);
  const inventoryIndex = useMemo(() => indexInventoryBySku(grid?.inventory ?? []), [grid?.inventory]);
  const derivedDemandIndex = useMemo(
    () => indexDerivedDemandByCell(grid?.derived_demand ?? []),
    [grid?.derived_demand],
  );
  const derivedDemandSkusByCategory = useMemo(
    () => indexDerivedDemandSkusByCategory(grid?.derived_demand_skus ?? []),
    [grid?.derived_demand_skus],
  );
  const kitItemsByCategory = useMemo(() => {
    const map = new Map<string, ClampForecastKitItemRow[]>();
    for (const item of grid?.kit_items ?? []) {
      const list = map.get(item.category_code) ?? [];
      list.push(item);
      map.set(item.category_code, list);
    }
    return map;
  }, [grid?.kit_items]);

  const kitComponentSkus = useMemo(() => {
    const unique = new Set<string>();
    for (const item of grid?.kit_items ?? []) {
      const sku = item.component_sku.trim();
      if (sku.length > 0) unique.add(sku);
    }
    return [...unique].slice(0, 500);
  }, [grid?.kit_items]);

  const kitSkuHoverHintsQuery = useQuery({
    queryKey: ["mrp", "sku-hover-hints", "clamp-forecast-kit", kitComponentSkus.join("\n")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: kitComponentSkus.join(",") },
      }),
    enabled: kitComponentSkus.length > 0,
    refetchOnWindowFocus: false,
  });

  const cellsById = useMemo(() => {
    const map = new Map<number, ClampForecastCellRow>();
    for (const cell of grid?.cells ?? []) {
      map.set(cell.id, cell);
    }
    return map;
  }, [grid?.cells]);

  const monthPoPlanByMonth = useMemo(() => {
    const map = new Map<string, ClampForecastMonthPoPlan>();
    if (grid == null) return map;
    const poLinksForMonthPlan = (grid.po_links ?? []).map((link) => ({
      forecast_id: link.forecast_id,
      sku: link.sku,
      quantity_ordered: link.quantity_ordered,
    }));
    const kitItemsForPlan = new Map<
      string,
      { component_sku: string; quantity_per_kit: number }[]
    >();
    for (const [categoryCode, items] of kitItemsByCategory.entries()) {
      kitItemsForPlan.set(
        categoryCode,
        items.map((item) => ({
          component_sku: item.component_sku,
          quantity_per_kit: item.quantity_per_kit,
        })),
      );
    }
    for (const month of grid.months) {
      map.set(
        month,
        buildClampForecastMonthPoPlan({
          plan_month: month,
          cells: grid.cells,
          categories: grid.categories,
          kitItemsByCategory: kitItemsForPlan,
          poLinks: poLinksForMonthPlan,
        }),
      );
    }
    return map;
  }, [grid, kitItemsByCategory]);

  const visibleCategories = useMemo(
    () => (grid?.categories ?? []).filter((category) => category.active),
    [grid?.categories],
  );

  const activeCellRow = useMemo(() => {
    if (activeCell == null) return null;
    return cellsIndex.get(cellKey(activeCell.category_code, activeCell.plan_month)) ?? null;
  }, [activeCell, cellsIndex]);

  const activeGhostRow = useMemo(() => {
    if (activeCell == null) return null;
    return ghostsIndex.get(cellKey(activeCell.category_code, activeCell.plan_month)) ?? null;
  }, [activeCell, ghostsIndex]);

  const activeCategoryRow = useMemo(() => {
    if (activeCell == null) return null;
    return (grid?.categories ?? []).find((category) => category.code === activeCell.category_code) ?? null;
  }, [activeCell, grid?.categories]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>Clamp forecast</PageTitleWithHelp>
      </header>

      {shellQuery.isError ? (
        <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          Could not load the clamp forecast grid: {describeApiError(shellQuery.error)}
        </div>
      ) : null}

      {derivedQuery.isError ? (
        <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Forecast quantities loaded, but the MRP roll-forward line could not be computed:{" "}
          {describeApiError(derivedQuery.error)}
        </div>
      ) : null}

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3 pb-2">
          <CardTitleWithHelp hoverHint={INVENTORY_HOVER_HINT}>Forecast grid</CardTitleWithHelp>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <label className="flex items-center gap-2">
              <span className="font-medium text-slate-600">Start month</span>
              <select
                className={START_MONTH_SELECT_CLASS}
                value={startMonth}
                onChange={(event) => {
                  const next = event.target.value;
                  setStartMonth(next);
                  writeClampForecastStartMonthToSession(next);
                }}
                aria-label="Clamp forecast grid start month"
              >
                {startMonthOptions.map((monthIso) => (
                  <option key={monthIso} value={monthIso}>
                    {formatMonth(monthIso)}
                  </option>
                ))}
              </select>
            </label>
            {grid ? (
              <span className="text-slate-500">
                {grid.months.length} months ({formatMonth(grid.start_month)} →{" "}
                {formatMonth(grid.months[grid.months.length - 1] ?? grid.start_month)})
              </span>
            ) : null}
            {hubWritesAllowed ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={clearGhostNumbersMutation.isPending}
                isLoading={clearGhostNumbersMutation.isPending}
                onClick={() => {
                  if (
                    !window.confirm(
                      "Delete all clamp forecast ghost shipment adjustments? This clears every what-if delta and updates the middle roll-forward line.",
                    )
                  ) {
                    return;
                  }
                  clearGhostNumbersMutation.mutate();
                }}
              >
                Clear all ghost numbers
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          {shell == null && shellQuery.isLoading ? (
            <div className="space-y-3 py-2" aria-busy="true" aria-label="Loading forecast grid">
              <div className="h-8 animate-pulse rounded bg-slate-100" />
              <div className="h-48 animate-pulse rounded bg-slate-50" />
            </div>
          ) : grid == null ? (
            <p className="text-sm text-slate-500">No data.</p>
          ) : (
            <ClampForecastTable
              months={grid.months}
              categories={visibleCategories}
              cellsIndex={cellsIndex}
              ghostsIndex={ghostsIndex}
              poLinksIndex={poLinksIndex}
              inventoryIndex={inventoryIndex}
              kitItemsByCategory={kitItemsByCategory}
              kitSkuHoverHints={kitSkuHoverHintsQuery.data?.sku_hover_hints}
              derivedDemandIndex={derivedDemandIndex}
              derivedDemandSkusByCategory={derivedDemandSkusByCategory}
              derivedDemandLoading={derivedDemandLoading}
              refreshingFrom={refreshingFrom}
              activeCell={activeCell}
              monthPoPlanByMonth={monthPoPlanByMonth}
              hubWritesAllowed={hubWritesAllowed}
              draggingPoLinkId={draggingPoLinkId}
              poLinkDropHighlightMonth={poLinkDropHighlightMonth}
              poLinkDragSource={
                activeCell != null && draggingPoLinkId != null
                  ? {
                      categoryCode: activeCell.category_code,
                      planMonth: activeCell.plan_month,
                    }
                  : null
              }
              poLinkDragSourceCellId={activeCellRow?.id ?? null}
              poLinkMovePending={movePoLinkMutation.isPending}
              onPoLinkDropHighlightMonth={setPoLinkDropHighlightMonth}
              onDropPoLinkOnMonth={(linkId, cellId, targetPlanMonth) => {
                movePoLinkMutation.mutate({ cellId, linkId, planMonth: targetPlanMonth });
              }}
              onSelectCell={setActiveCell}
              onOpenMonthPo={setActiveMonthPo}
            />
          )}
        </CardBody>
      </Card>

      {activeMonthPo != null ? (
        <ClampForecastMonthCreatePoModal
          planMonth={activeMonthPo}
          plan={monthPoPlanByMonth.get(activeMonthPo) ?? null}
          cellsById={cellsById}
          onClose={() => setActiveMonthPo(null)}
          onMonthGridStale={handleMonthGridStale}
          onError={(message) => showToast({ message, tone: "error" })}
          onSuccess={(message) => showToast({ message, tone: "success" })}
        />
      ) : null}

      {activeCell != null && activeCategoryRow != null ? (
        <AnchoredDetailCallout
          anchorSelector={buildAttributeAnchorSelector(
            "data-clamp-forecast-cell",
            cellKey(activeCell.category_code, activeCell.plan_month),
          )}
          onClose={() => setActiveCell(null)}
          panelWidth={CALLOUT_WIDTH}
          ariaLabel="Forecast cell detail"
          measureKey={cellKey(activeCell.category_code, activeCell.plan_month)}
        >
          <ClampForecastSidePanel
            category={activeCategoryRow}
            planMonth={activeCell.plan_month}
            cell={activeCellRow}
            ghost={activeGhostRow}
            poLinks={
              activeCellRow != null
                ? poLinksIndex.get(activeCellRow.id) ?? []
                : []
            }
            kitItems={kitItemsByCategory.get(activeCategoryRow.code) ?? []}
            derivedDemandRows={
              derivedDemandIndex.get(cellKey(activeCell.category_code, activeCell.plan_month)) ?? []
            }
            hubWritesAllowed={hubWritesAllowed}
            draggingPoLinkId={draggingPoLinkId}
            poLinkMovePending={movePoLinkMutation.isPending}
            onDragPoLinkStart={setDraggingPoLinkId}
            onDragPoLinkEnd={() => {
              setDraggingPoLinkId(null);
              setPoLinkDropHighlightMonth(null);
            }}
            onClose={() => setActiveCell(null)}
            onCellSaved={handleCellSaved}
            onCellCleared={handleCellCleared}
            onGhostSaved={handleGhostSaved}
            onGridStale={() => handleGridStale(activeCategoryRow.code, activeCell.plan_month)}
            onError={(message) => showToast({ message, tone: "error" })}
            onSuccess={(message) => showToast({ message, tone: "success" })}
          />
        </AnchoredDetailCallout>
      ) : null}
    </div>
  );
}
